import { BookingCode, LeasmanContractService, Validation, Vehicle, VehicleAssessment } from "../../../system/Domain";
import { effectiveFactor, isManualGoodwill } from "./damageCalculation";
import Tile from "../../../Components/Tiles/Tile";
import TileContent from "../../../Components/Tiles/TileContent";
import { Box, Tooltip, useTheme } from "@mui/material";
import TextBlock from "../../../Components/Layout/TextBlock";
import { Info } from "@mui/icons-material";
import formatCurrency from "../../../system/formatCurrency";
import { Dispatch, SetStateAction, useEffect } from "react";
import ListItemChip from "../../../Components/ListItemChip";
import ListItemChips from "../../../Components/ListItemChips";
import AssessmentDamagesTable from "./AssessmentDamagesTable";
import ServiceChips, { isServiceValid } from "./ServiceChips";
import SpecialMarkerChips from "./SpecialMarkerChips";
import { ValidationDeductionCalculationType } from "../RemarketingLayout";
import moment from "moment";
import dateFormats from "../../../system/dateFormats";
import { translateDamageCatalog, translateDepreciationCalculationType } from "../../../system/translate";

interface Props {
	assessment: VehicleAssessment
	vehicle: Vehicle
	bookingCodes: BookingCode[]
	validation: Validation
	setValidation: Dispatch<SetStateAction<Validation>>
	disabled: boolean
	discountValue: number
	calculationType: ValidationDeductionCalculationType
}

export const lasmanContractServiceIsEnsurance = (v: LeasmanContractService) => {
	return v.type?.id == "HF" || v.type?.id == "VK" || v.type?.id == "V4" || v.type?.id == "V6" || v.type?.id == "S1" || v.type?.id == "S2" || v.type?.id == "S3";
};

const AssessmentDamages = (props: Props) => {
	const theme = useTheme();

	const contractEndDate = props.validation.contractEndDate;

	const hasTireService = props.vehicle.remarketing.leasmanContract?.services?.some(v => v.type?.id == "RF" && v.isOpen === false && isServiceValid(v, contractEndDate));
	const hasMechanicalService = props.vehicle.remarketing.leasmanContract?.services?.some(v => v.type?.id == "VE" && v.isOpen === false && isServiceValid(v, contractEndDate));
	const hasInsurance = props.vehicle.remarketing.leasmanContract?.services?.some(v => lasmanContractServiceIsEnsurance(v) && isServiceValid(v, contractEndDate));

	const listPriceNotAvailable = props.vehicle.type.costs.listPrice.netAmount <= 0;

	const damageList = props.validation.damages;
	const damageCalculationList = props.validation.damages?.filter(f => f.bookingCode.key !== "RU02-X");

	// These sums are replicated in the backend. Any change has to be done there as well!
	const repairCosts = damageCalculationList?.filter(f => !f.bookingCode.allowance && !isManualGoodwill(f))
		.reduce((sum, item) => sum + item.netAmount, 0);

	const absDepreciationAmount =
		damageCalculationList
			?.filter(f => !f.bookingCode.allowance && !isManualGoodwill(f))
			.reduce(
				(sum, item) =>
					sum +
					Math.round(Math.abs(item.netAmount * effectiveFactor(item)) * 100),
				0
			) / 100;

	const finalCustomerBill =
		damageCalculationList
			?.filter(item => item.bookingCode.factor > 0 || item.bookingCode.allowance || isManualGoodwill(item))
			.reduce((sum, item) => {
				const lineAmount = item.useAssessmentNetValues
					? Math.abs(item.depreciationAmountNet) * item.bookingCode.factor
					: item.netAmount * effectiveFactor(item) * item.bookingCode.factor;

				return sum + Math.round(lineAmount * 100);
			}, 0) / 100;

	const notForCustomer =
		damageCalculationList
			?.filter(f => !f.bookingCode.allowance)
			.filter(item => item.bookingCode.factor < 0)
			.reduce((sum, item) => {
				const lineAmount = item.useAssessmentNetValues
					? Math.abs(item.depreciationAmountNet) * item.bookingCode.factor
					: item.netAmount * effectiveFactor(item) * item.bookingCode.factor;

				return sum + Math.round(lineAmount * 100);
			}, 0) / 100;

	const allowance = damageCalculationList?.filter(item => item.bookingCode.allowance)
		.reduce((sum, item) => sum + item.netAmount, 0);

	useEffect(() => {
		if (props.validation.finalCustomerBill !== finalCustomerBill
			|| props.validation.repairCosts !== repairCosts
			|| props.validation.absDepreciationAmount !== absDepreciationAmount
			|| props.validation.notForCustomer !== notForCustomer
			|| props.validation.allowance !== allowance
		)
			props.setValidation({
				...props.validation,
				repairCosts: repairCosts,
				absDepreciationAmount: absDepreciationAmount,
				finalCustomerBill: finalCustomerBill,
				notForCustomer: notForCustomer,
				allowance: allowance
			});
	}, [props.validation]);

	const customer = props.vehicle?.remarketing?.leasmanContract?.customer;

	return (
		<Tile title={
			<Box sx={{ display: "flex", justifyContent: "space-between", alignContent: "center", alignItems: "center" }}>
				<Box>Schäden aus dem Gutachten</Box>
				<ListItemChips>
					<ServiceChips
						leasmanContractServices={props.vehicle.remarketing.leasmanContract?.services?.filter(s => ((s.type?.id === "VE" || s.type?.id === "RF") && s.isOpen === false || lasmanContractServiceIsEnsurance(s)))}
						contractEndDate={contractEndDate}
					/>
					{customer && (
						<ListItemChip
							label={customer.hasFrameworkAgreement ? "Rahmenvertrag" : "AGB Vertrag"}
							color={customer.hasFrameworkAgreement ? "primary" : "info"}
							title={customer.hasFrameworkAgreement ? customer.frameworkAgreement : undefined}
						/>
					)}
					<ListItemChip label={translateDamageCatalog(props.vehicle.remarketing.leasmanContract?.damageCatalog)} title="Schadenskatalog" />
					<ListItemChip label={translateDepreciationCalculationType(props.vehicle.remarketing.leasmanContract?.depreciationCalculationType)} title="Minderwertberechnung" />
					<SpecialMarkerChips vehicleAssessment={props.assessment} />
					{props.validation.isStopped && (
						<ListItemChip
							label="Validationsstopp"
							title="Es gibt einen aktiven Validationsstopp der die Übertragung und damit finale Beendigung der Validierung verhindert."
							color="error"
						/>
					)}
				</ListItemChips>
			</Box>
		}>
			<TileContent dense>
				<Box p={2} pb={4}>
					<Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
						<Box sx={{ display: "flex", alignContent: "middle", justifyContent: "space-between", width: "100%" }}>
							<Box sx={{ paddingLeft: 2 }}>
								{damageList?.length || 0} Positionen gefunden
							</Box>
							<Box sx={{ fontStyle: "italic" }}>
								Alle ausgewiesenen Beträge verstehen sich netto.
							</Box>
							<Box sx={{
								display: "flex",
								alignContent: "middle",
								paddingRight: 2
							}}>
								<Box sx={{ color: listPriceNotAvailable && props.calculationType === "DeductionFormula" ? "secondary.main" : "inherit" }}>
									{props.discountValue && (
										<>{props.discountValue}%&nbsp;</>
									)}
								</Box>
								<Tooltip
									sx={{
										...((props.calculationType === "AyvensTable" || props.calculationType === "LpdeTable") && !props.discountValue && {color: "secondary.main"})
									}}
									title={
										<>
											{(props.calculationType === "DeductionFormula" || props.calculationType === "Precalculated") && (
												<Box>
													<Box>Händlerverkaufspreis: {formatCurrency(props.assessment.market2Price)}</Box>
													<Box sx={{ color: listPriceNotAvailable ? "secondary.main" : "inherit" }}>
														Listenpreis: {formatCurrency(props.vehicle.type.costs.listPrice.netAmount)}
													</Box>
												</Box>
											)}
											{props.calculationType === "AyvensTable" && (
												<Box>
													<Box mb={2}>Abwertung nach Ayvens Abwertungstabelle</Box>
													<Box>Erstzulassung: {moment(props.vehicle.dateFirstRegistered).format(dateFormats.date)}</Box>
													<Box>
														Kilometerstand: <Box component="span"  sx={{ color: !props.validation?.mileage ? "secondary.main" : "inherit" }}>{props.validation?.mileage || "Bitte unten auswählen"}</Box>
													</Box>
													<Box>
														Vertragsende: <Box component="span" sx={{ color: !props.validation?.contractEndDate ? "secondary.main" : "inherit" }}>{props.validation?.contractEndDate ? moment(props.validation.contractEndDate).format(dateFormats.date) : "Bitte unten auswählen"}</Box>
													</Box>
												</Box>
											)}
											{props.calculationType === "LpdeTable" && (
												<Box>
													<Box mb={2}>Abwertung nach Leaseplan DE Abwertungstabelle</Box>
													<Box>Erstzulassung: {moment(props.vehicle.dateFirstRegistered).format(dateFormats.date)}</Box>
													<Box>
														Kilometerstand: <Box component="span"  sx={{ color: !props.validation?.mileage ? "secondary.main" : "inherit" }}>{props.validation?.mileage || "Bitte unten auswählen"}</Box>
													</Box>
													<Box>
														Vertragsende: <Box component="span" sx={{ color: !props.validation?.contractEndDate ? "secondary.main" : "inherit" }}>{props.validation?.contractEndDate ? moment(props.validation.contractEndDate).format(dateFormats.date) : "Bitte unten auswählen"}</Box>
													</Box>
												</Box>
											)}
										</>
									}
								>
									<Info />
								</Tooltip>
							</Box>
						</Box>
					</Box>
					<AssessmentDamagesTable
						damageList={props.validation.damages}
						discountValue={props.discountValue}
						setValidation={props.setValidation}
						hasMechanicalService={hasMechanicalService}
						hasTireService={hasTireService}
						hasInsurance={hasInsurance}
						bookingCodes={props.bookingCodes}
						disabled={props.disabled}
						contractEndDate={contractEndDate}
						calculationType={props.calculationType}
					/>
					<hr />
					<Box sx={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between", paddingLeft: 3, paddingRight: 3 }}>
						<TextBlock
							align="right"
							primary={formatCurrency(repairCosts)}
							secondary="Reparaturkosten"
						/>
						<TextBlock
							align="right"
							primary={formatCurrency(absDepreciationAmount)}
							secondary="Absolute Minderwerte"
						/>
						<TextBlock
							align="right"
							primary={formatCurrency(allowance)}
							secondary="Freibetrag"
						/>
						<TextBlock
							align="right"
							primary={formatCurrency(notForCustomer)}
							secondary={
								<>Minderwerte <span style={{ color: theme.palette.secondary.main }}>nicht</span> z.L. des Kunden</>
							}
						/>
						<TextBlock
							align="right"
							primary={formatCurrency(finalCustomerBill)}
							secondary="Minderwerte z.L. des Kunden"
						/>
					</Box>
				</Box>
			</TileContent>
		</Tile>
	);
};

export default AssessmentDamages;
